/**
 * Feature: Look Book Modal
 * Responsibility: Handles all interactions for the lookbook drawer.
 *
 * Architecture notes:
 * - All DOM queries use data-* attributes. No class or tag selectors.
 * - Business logic is pure and receives DOM nodes as explicit arguments.
 * - The document capture-phase listener is required to intercept Shopify's
 *   component.js zoom handler, which also listens in capture phase on document.
 *   Calling stopImmediatePropagation() here prevents the zoom from firing.
 */

// ---------------------------------------------------------------------------
// DOM Query helpers — centralised, guarded
// ---------------------------------------------------------------------------

/**
 * @returns {Element|null}
 */
function queryModal() {
  return document.querySelector('[data-lookbook-modal]');
}

/**
 * @param {Element} formWrapper
 * @returns {HTMLInputElement|null}
 */
function queryVariantIdInput(formWrapper) {
  return formWrapper.querySelector('[data-lookbook-variant-input]');
}

/**
 * @param {Element} formWrapper
 * @returns {{ button: Element|null, label: Element|null }}
 */
function queryAddButton(formWrapper) {
  return {
    button: formWrapper.querySelector('[data-lookbook-add-button]'),
    label: formWrapper.querySelector('[data-lookbook-add-button-label]'),
  };
}

// ---------------------------------------------------------------------------
// Business logic — pure, no DOM side-effects
// ---------------------------------------------------------------------------

/**
 * Opens the lookbook dialog.
 * @param {Element} modal - The dialog-component element.
 */
function openModal(modal) {
  if (typeof modal.showDialog === 'function') {
    modal.showDialog();
    return;
  }
  const dialog = modal.querySelector('dialog');
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
  }
}

/**
 * Scrolls the slider by one full viewport width in the given direction.
 * @param {Element} slider - The scrollable slider track element.
 * @param {'prev'|'next'} direction
 */
function scrollSlider(slider, direction) {
  const delta = direction === 'prev' ? -slider.clientWidth : slider.clientWidth;
  slider.scrollBy({ left: delta, behavior: 'smooth' });
}

/**
 * Updates the hidden variant id input when the user selects a size.
 * @param {HTMLInputElement} idInput
 * @param {string} variantId
 */
function updateVariantId(idInput, variantId) {
  idInput.value = variantId;
}

/**
 * Updates the add-to-cart button availability state.
 * @param {{ button: Element|null, label: Element|null }} refs
 * @param {boolean} isAvailable
 */
function updateAddButton(refs, isAvailable) {
  if (!refs.button || !refs.label) return;
  refs.button.toggleAttribute('disabled', !isAvailable);
  refs.label.textContent = isAvailable ? 'Add to Cart' : 'Sold out';
}

// ---------------------------------------------------------------------------
// Event wiring — document-level, capture phase for modal open
// ---------------------------------------------------------------------------

/**
 * Capture-phase click handler.
 * Intercepts clicks from inside <lookbook-trigger> before Shopify's component.js
 * capture listener can route them to the zoom handler.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('lookbook-trigger');
    if (!trigger) return;

    // Prevent zoom and any other document capture listeners from firing.
    event.preventDefault();
    event.stopImmediatePropagation();

    const modal = queryModal();
    if (!modal) return;

    openModal(modal);
  },
  { capture: true }
);

/**
 * Capture-phase pointer/mouse handlers.
 * Prevents theme drag and swipe handlers from activating on the trigger press.
 */
['pointerdown', 'mousedown'].forEach((eventName) => {
  document.addEventListener(
    eventName,
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('lookbook-trigger')) {
        event.stopImmediatePropagation();
      }
    },
    { capture: true }
  );
});

/**
 * Bubble-phase click handler for slider arrow navigation.
 * Uses data-* attributes exclusively for element identification.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const isPrev = target.closest('[data-lookbook-prev]');
  const isNext = target.closest('[data-lookbook-next]');

  if (!isPrev && !isNext) return;

  event.preventDefault();
  event.stopPropagation();

  const direction = isPrev ? 'prev' : 'next';
  const arrowBtn = isPrev || isNext;
  const sliderTrack = arrowBtn
    .closest('[data-lookbook-slider-wrapper]')
    ?.querySelector('[data-lookbook-slider]');

  if (!sliderTrack) return;

  scrollSlider(sliderTrack, direction);
});

/**
 * Variant option button click handler.
 * Manages visual selection state, updates the hidden variant id input,
 * and updates the add-to-cart button state.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const optionBtn = target.closest('[data-lookbook-option-button]');
  if (!optionBtn) return;

  const isAvailable = optionBtn.dataset.lookbookAvailable === 'true';
  const variantId = optionBtn.dataset.lookbookVariantId;
  const optionGroup = optionBtn.closest('[data-lookbook-option-group]');
  const formWrapper = optionBtn.closest('[data-lookbook-form-wrapper]');

  if (!optionGroup || !formWrapper) return;

  // Update visual selection state
  optionGroup.querySelectorAll('[data-lookbook-option-button]').forEach((btn) => {
    btn.classList.remove('is-selected');
  });
  optionBtn.classList.add('is-selected');

  // Update form variant id
  const idInput = queryVariantIdInput(formWrapper);
  if (idInput && variantId) {
    updateVariantId(idInput, variantId);
  }

  // Update add-to-cart button
  const addButtonRefs = queryAddButton(formWrapper);
  updateAddButton(addButtonRefs, isAvailable);
});

// ---------------------------------------------------------------------------
// Quantity button handlers
// ---------------------------------------------------------------------------
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const minusBtn = target.closest('[data-lookbook-qty-minus]');
  const plusBtn = target.closest('[data-lookbook-qty-plus]');
  
  if (!minusBtn && !plusBtn) return;
  
  const qtyWrapper = target.closest('[data-lookbook-quantity]');
  if (!qtyWrapper) return;
  
  const input = qtyWrapper.querySelector('[data-lookbook-qty-input]');
  if (!input) return;

  let currentValue = parseInt(input.value || '1', 10);
  if (isNaN(currentValue)) currentValue = 1;

  const min = parseInt(input.getAttribute('min') || '1', 10);
  const max = parseInt(input.getAttribute('max') || '10', 10);

  if (minusBtn) {
    currentValue = Math.max(min, currentValue - 1);
  } else if (plusBtn) {
    currentValue = Math.min(max, currentValue + 1);
  }

  input.value = currentValue;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-lookbook-qty-input]')) return;

  const min = parseInt(target.getAttribute('min') || '1', 10);
  const max = parseInt(target.getAttribute('max') || '10', 10);
  let val = parseInt(target.value, 10);

  if (isNaN(val) || val < min) {
    target.value = min;
  } else if (val > max) {
    target.value = max;
  }
});

// ---------------------------------------------------------------------------
// Custom element — minimal, semantic grouping only
// ---------------------------------------------------------------------------
class LookbookTrigger extends HTMLElement {}

if (!customElements.get('lookbook-trigger')) {
  customElements.define('lookbook-trigger', LookbookTrigger);
}
